import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import InvoiceModal from '../../components/admin/InvoiceModal';
import { posApi, operationApi } from '../../services/api';
import * as XLSX from 'xlsx';
import { QrCode, UserCheck, Tag, MessageCircle, Clock, UserX } from 'lucide-react';

const STATUSES = ['pending', 'assigned', 'waiting', 'confirmed', 'on_progress', 'done', 'invoice_release', 'paid', 'invoice_outstanding', 'cancelled'];
const PAYMENT_STATUSES = ['unpaid', 'outstanding', 'paid', 'refunded'];
const PAYMENT_METHODS = ['cash', 'transfer', 'qris', 'other'];

const BUSY_ORDER_STATUSES = ['assigned', 'waiting', 'confirmed', 'on_progress'];

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700', assigned: 'bg-indigo-100 text-indigo-700',
  waiting: 'bg-orange-100 text-orange-700', confirmed: 'bg-blue-100 text-blue-700',
  on_progress: 'bg-purple-100 text-purple-700', done: 'bg-green-100 text-green-700',
  invoice_release: 'bg-teal-100 text-teal-700', paid: 'bg-emerald-100 text-emerald-700',
  invoice_outstanding: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-700',
};
const paymentColors = {
  unpaid: 'bg-red-100 text-red-700', outstanding: 'bg-orange-100 text-orange-700',
  paid: 'bg-green-100 text-green-700', refunded: 'bg-gray-100 text-gray-700',
};

const statusLabels = {
  pending: 'Pending', assigned: 'Diassign', waiting: 'Waiting', confirmed: 'Confirmed',
  on_progress: 'On Progress', done: 'Done', invoice_release: 'Invoice Release',
  paid: 'Paid', invoice_outstanding: 'Invoice Outstanding', cancelled: 'Cancelled',
};
const paymentLabels = {
  unpaid: 'Belum Bayar', outstanding: 'Outstanding', paid: 'Lunas', refunded: 'Refund',
};

function waLink(order, formatPrice) {
  const phone = (order.customer_phone || '').replace(/[^0-9]/g, '');
  const items = order.items || [];
  const itemLines = items.map((item, idx) => {
    const priceStr = formatPrice(item.unit_price * (item.quantity || 1));
    const durationStr = item.duration ? ` — ${item.duration} menit` : '';
    return `${idx + 1}. ${item.name} ×${item.quantity || 1} = ${priceStr}${durationStr}`;
  }).join('\n');
  const totalDuration = items.reduce((sum, item) => sum + (parseInt(item.duration) || 0) * (item.quantity || 1), 0);
  const durationLine = totalDuration > 0 ? `\n⏱ Est. total pengerjaan: ${totalDuration} menit\n` : '\n';
  const text = encodeURIComponent(
    `Halo ${order.customer_name},\n\n` +
    `Terima kasih telah memesan layanan Klinnovac.\n\n` +
    `Berikut detail pesanan Anda:\n` +
    `Nomor Order: ${order.order_number}\n\n` +
    `📋 Layanan:\n${itemLines}\n` +
    `\n💰 Total: ${formatPrice(order.total)}` +
    `${durationLine}` +
    `\nTim kami akan segera menghubungi Anda.`
  );
  return `https://wa.me/${phone}?text=${text}`;
}

function getActiveOrderCounts(orders) {
  const counts = {};
  orders.forEach(o => {
    if (o.assigned_worker_id && BUSY_ORDER_STATUSES.includes(o.status)) {
      counts[o.assigned_worker_id] = (counts[o.assigned_worker_id] || 0) + 1;
    }
  });
  return counts;
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 15 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', payment_status: '', payment_method: '', search: '' });
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [assignLoading, setAssignLoading] = useState({});
  const [busyWorkerIds, setBusyWorkerIds] = useState(new Set());
  const [paymentModal, setPaymentModal] = useState(null); // { orderId, value, note, image }

  const fetchOrders = async (page = 1) => {
    setLoading(true);
    try {
      const params = { per_page: 15, page };
      if (filters.status) params.status = filters.status;
      if (filters.payment_status) params.payment_status = filters.payment_status;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      if (filters.search) params.search = filters.search;
      const res = await posApi.orders.list(params);
      const responseData = res.data.data;
      const list = responseData?.data || responseData || [];
      setOrders(list);
      // Calculate busy workers from current page
      const counts = getActiveOrderCounts(list);
      setBusyWorkerIds(new Set(Object.keys(counts).map(Number)));
      setPagination({ current_page: responseData?.current_page || 1, last_page: responseData?.last_page || 1, total: responseData?.total || 0, per_page: responseData?.per_page || 15 });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(1); }, [filters]);

  // Also refresh worker availability when detail changes
  useEffect(() => {
    if (!showDetail) return;
    const counts = getActiveOrderCounts(orders);
    setBusyWorkerIds(new Set(Object.keys(counts).map(Number)));
  }, [showDetail, orders]);

  useEffect(() => {
    const loadWorkers = async () => {
      try { const res = await operationApi.profiles.list({ per_page: 200 }); setWorkers(res.data.data?.data || res.data.data || []); } catch {}
    };
    loadWorkers();
  }, []);

  const handleStatusUpdate = async (orderId, field, value) => {
    // Intercept payment_status changes to paid/outstanding — require note + optional image
    if (field === 'payment_status' && (value === 'paid' || value === 'outstanding')) {
      setPaymentModal({ orderId, value, note: '', image: null, imagePreview: null });
      return;
    }
    setSaving(true);
    try { await posApi.orders.update(orderId, { [field]: value }); fetchOrders(pagination.current_page); if (showDetail?.id === orderId) setShowDetail(prev => ({ ...prev, [field]: value })); }
    catch (err) { alert('Gagal update'); } finally { setSaving(false); }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentModal || !paymentModal.note.trim()) {
      alert('Catatan wajib diisi saat mengubah status pembayaran ke Lunas / Outstanding.');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('payment_status', paymentModal.value);
      formData.append('payment_note', paymentModal.note);
      if (paymentModal.image) {
        formData.append('payment_receipt', paymentModal.image);
      }
      await posApi.orders.update(paymentModal.orderId, formData);
      setPaymentModal(null);
      fetchOrders(pagination.current_page);
      if (showDetail?.id === paymentModal.orderId) setShowDetail(prev => ({ ...prev, payment_status: paymentModal.value }));
    } catch (err) { alert('Gagal update'); } finally { setSaving(false); }
  };

  const handleAssignWorker = async (orderId, workerId) => {
    setAssignLoading(prev => ({ ...prev, [orderId]: true }));
    try {
      await posApi.orders.update(orderId, { assigned_worker_id: workerId, status: 'assigned' });
      fetchOrders(pagination.current_page);
      if (showDetail?.id === orderId) setShowDetail(prev => ({ ...prev, assigned_worker_id: workerId, status: 'assigned' }));
    } catch (err) { alert('Gagal assign worker'); }
    finally { setAssignLoading(prev => ({ ...prev, [orderId]: false })); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Hapus pesanan ini?')) return; try { await posApi.orders.delete(id); fetchOrders(pagination.current_page); setShowDetail(null); } catch (err) { alert('Gagal menghapus'); } };

  const handleExport = async () => {
    try {
      const res = await posApi.orders.list({ per_page: 9999 }); const all = res.data.data?.data || res.data.data || [];
      const data = all.map(o => ({
        'No Order': o.order_number, 'Pelanggan': o.customer_name, 'Telepon': o.customer_phone,
        'Subtotal': o.subtotal, 'Pajak': o.tax_amount, 'Diskon': o.discount_amount, 'Total': o.total,
        'Status': statusLabels[o.status] || o.status, 'Pembayaran': paymentLabels[o.payment_status] || o.payment_status,
        'Metode': o.payment_method, 'Referral': o.referral_code || '-', 'Worker': o.assigned_worker?.full_name || '-',
        'Tanggal': fmtDate(o.created_at),
      }));
      const ws = XLSX.utils.json_to_sheet(data); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Pesanan'); XLSX.writeFile(wb, 'daftar-pesanan.xlsx');
    } catch (err) { alert('Gagal export'); }
  };

  const formatPrice = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(p);
  const fmtDate = (d) => new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const getReferralWorker = (order) => {
    if (order.referral_code) { const w = workers.find(w => w.card_code === order.referral_code); return w || null; }
    return null;
  };

  const isWorkerBusy = (workerId, currentOrderId) => {
    // If this worker is already assigned to this order (currentOrderId), they're not "busy" for this same order
    // But if they're busy on ANOTHER order, they are busy
    if (currentOrderId) {
      const busyForOther = orders.some(o => o.id !== currentOrderId && o.assigned_worker_id === workerId && BUSY_ORDER_STATUSES.includes(o.status));
      return busyForOther;
    }
    return busyWorkerIds.has(workerId);
  };

  const workerBusyDetails = (workerId) => {
    const busyOrders = orders.filter(o => o.assigned_worker_id === workerId && BUSY_ORDER_STATUSES.includes(o.status));
    return busyOrders.map(o => `#${o.order_number} (${statusLabels[o.status]})`).join(', ');
  };

  const pages = []; for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) { pages.push(i); }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6"><h1 className="text-lg font-semibold text-gray-800 mb-1">Pesanan</h1><button onClick={handleExport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">📥 Export Excel</button></div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Status</label><select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">Semua</option>{STATUSES.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Pembayaran</label><select value={filters.payment_status} onChange={e => setFilters({...filters, payment_status: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">Semua</option>{PAYMENT_STATUSES.map(s => <option key={s} value={s}>{paymentLabels[s] || s}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Metode Bayar</label><select value={filters.payment_method} onChange={e => setFilters({...filters, payment_method: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">Semua</option>{PAYMENT_METHODS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Cari</label><input placeholder="#order / nama / telp" value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
          </div>
        </div>

        {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (<>
          <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pelanggan</th><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bayar</th><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referral</th><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th><th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tgl</th><th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th></tr></thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((order) => {
                  const refWorker = getReferralWorker(order);
                  const assignedWorker = order.assigned_worker || workers.find(w => w.id === order.assigned_worker_id) || null;
                  return (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-xs font-mono text-gray-900">{order.order_number}</td>
                    <td className="px-3 py-3">
                      <p className="text-sm font-medium text-gray-900">{order.customer_name}</p>
                      <a href={waLink(order, formatPrice)} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-800 flex items-center gap-0.5">
                        <MessageCircle size={11} /> {order.customer_phone}
                      </a>
                    </td>
                    <td className="px-3 py-3 text-sm font-medium text-gray-900">{formatPrice(order.total)}</td>
                    <td className="px-3 py-3">
                      <select value={order.status} onChange={e => handleStatusUpdate(order.id, 'status', e.target.value)} className={`px-2 py-1 text-xs rounded-full border-0 font-medium ${statusColors[order.status] || ''}`}>
                        {STATUSES.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      <select value={order.payment_status} onChange={e => handleStatusUpdate(order.id, 'payment_status', e.target.value)} className={`px-2 py-1 text-xs rounded-full border-0 font-medium ${paymentColors[order.payment_status] || ''}`}>
                        {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{paymentLabels[s] || s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      {refWorker ? (<div className="flex items-center gap-1" title={`Referral dari QR: ${order.referral_code}`}><QrCode size={12} className="text-brand" /><span className="text-xs text-navy font-medium">{refWorker.full_name}</span></div>) : order.referral_code ? (<span className="text-xs text-gray-400">{order.referral_code}</span>) : (<span className="text-xs text-gray-300">-</span>)}
                    </td>
                    <td className="px-3 py-3">
                      {assignedWorker && BUSY_ORDER_STATUSES.includes(order.status) ? (
                        <div className="flex items-center gap-1">
                          <UserCheck size={12} className="text-indigo-500" />
                          <span className="text-xs font-medium text-indigo-700">{assignedWorker.full_name}</span>
                          <span className="text-[9px] bg-green-100 text-green-700 rounded-full px-1.5 py-0.5 font-medium" title="Sedang mengerjakan">{statusLabels[order.status]}</span>
                        </div>
                      ) : assignedWorker ? (
                        <div className="flex items-center gap-1"><UserCheck size={12} className="text-gray-400" /><span className="text-xs font-medium text-gray-600">{assignedWorker.full_name}</span></div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <select value="" onChange={e => { if (e.target.value) handleAssignWorker(order.id, parseInt(e.target.value)); }} className="w-28 border border-gray-200 rounded px-1 py-0.5 text-xs text-gray-500" disabled={assignLoading[order.id]}>
                            <option value="">{assignLoading[order.id] ? '...' : 'Assign'}</option>
                            {workers.filter(w => w.status === 'active').map(w => {
                              const busy = isWorkerBusy(w.id, order.id);
                              return (
                                <option key={w.id} value={w.id} disabled={busy} title={busy ? `Sedang sibuk: ${workerBusyDetails(w.id)}` : ''}>
                                  {w.full_name}{busy ? ' 🔴 Sibuk' : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500">{fmtDate(order.created_at)}</td>
                    <td className="px-3 py-3 text-right space-x-1">
                      <a href={waLink(order, formatPrice)} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800 text-xs" title="Chat WA Customer">💬</a>
                      <button onClick={() => setShowDetail(order)} className="text-blue-600 hover:text-blue-800 text-xs">Detail</button>
                      <button onClick={() => setInvoiceOrder(order)} className="text-navy hover:text-brand-dark text-xs">Invoice</button>
                      <button onClick={() => handleDelete(order.id)} className="text-red-600 hover:text-red-800 text-xs">Hapus</button>
                    </td>
                  </tr>
                  );
                })}
                {orders.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Belum ada pesanan</td></tr>}
              </tbody>
            </table>
          </div>
          {pagination.last_page > 1 && (
            <div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3">
              <span className="text-sm text-gray-500">Total {pagination.total} · Halaman {pagination.current_page} dari {pagination.last_page}</span>
              <div className="flex items-center gap-1">
                <button disabled={pagination.current_page === 1} onClick={() => fetchOrders(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">«</button>
                {pages.map(p => <button key={p} onClick={() => fetchOrders(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300'}`}>{p}</button>)}
                <button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchOrders(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">»</button>
              </div>
            </div>
          )}
        </>)}
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10"><h2 className="text-lg font-bold text-gray-800">{showDetail.order_number}</h2><button onClick={() => setShowDetail(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button></div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-xs text-gray-400">Pelanggan</p><p className="font-semibold">{showDetail.customer_name}</p></div>
                <div><p className="text-xs text-gray-400">Telepon</p>
                  <a href={waLink(showDetail, formatPrice)} target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark flex items-center gap-1 font-medium">
                    <MessageCircle size={12} /> {showDetail.customer_phone}
                  </a>
                </div>
                {showDetail.customer_email && <div><p className="text-xs text-gray-400">Email</p><p>{showDetail.customer_email}</p></div>}
                {showDetail.customer_address && <div><p className="text-xs text-gray-400">Alamat</p><p>{showDetail.customer_address}</p></div>}
                {showDetail.preferred_day && <div><p className="text-xs text-gray-400">📅 Hari Pemesanan</p><p className="font-semibold capitalize">{showDetail.preferred_day}</p></div>}
                {showDetail.preferred_time && <div><p className="text-xs text-gray-400">🕐 Jam Pemesanan</p><p className="font-semibold">{showDetail.preferred_time}</p></div>}
                {showDetail.notes && <div className="col-span-2"><p className="text-xs text-gray-400">Catatan</p><p className="text-sm">{showDetail.notes}</p></div>}
              </div>
              {showDetail.referral_code && (
                <div className="bg-brand/5 border border-brand/20 rounded-lg p-4"><p className="text-xs font-semibold text-brand mb-2 flex items-center gap-1"><QrCode size={14} /> Order via Referral QR</p><p className="text-sm text-gray-700">Kode: <span className="font-mono font-bold">{showDetail.referral_code}</span></p>{getReferralWorker(showDetail) && <p className="text-xs text-gray-500 mt-1">Dari: {getReferralWorker(showDetail).full_name} ({getReferralWorker(showDetail).employee_id})</p>}</div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div><p className="text-xs text-gray-400 mb-1">Status Pesanan</p><select value={showDetail.status} onChange={e => { handleStatusUpdate(showDetail.id, 'status', e.target.value); }} className={`w-full px-3 py-2 text-sm rounded-lg border-0 font-medium ${statusColors[showDetail.status] || 'bg-gray-100'}`}>{STATUSES.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}</select></div>
                <div><p className="text-xs text-gray-400 mb-1">Pembayaran</p><select value={showDetail.payment_status} onChange={e => { handleStatusUpdate(showDetail.id, 'payment_status', e.target.value); }} className={`w-full px-3 py-2 text-sm rounded-lg border-0 font-medium ${paymentColors[showDetail.payment_status] || 'bg-gray-100'}`}>{PAYMENT_STATUSES.map(s => <option key={s} value={s}>{paymentLabels[s] || s}</option>)}</select></div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Assign ke Pekerja</p>
                  <select
                    value={showDetail.assigned_worker_id || ''}
                    onChange={e => handleAssignWorker(showDetail.id, e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">- Belum diassign -</option>
                    {workers.filter(w => w.status === 'active').map(w => {
                      const busy = isWorkerBusy(w.id, showDetail.id);
                      return (
                        <option key={w.id} value={w.id} disabled={busy} title={busy ? `Sedang sibuk: ${workerBusyDetails(w.id)}` : ''}>
                          {w.full_name} ({w.employee_id}){busy ? ' 🔴 Sibuk' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {showDetail.assigned_worker && <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1"><UserCheck size={12} /> Diassign: {showDetail.assigned_worker.full_name}</p>}
                  {!showDetail.assigned_worker && showDetail.assigned_worker_id && <p className="text-xs text-indigo-600 mt-1">Worker ID: {showDetail.assigned_worker_id}</p>}
                </div>
              </div>

              {/* Items with duration */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><Tag size={14} /> Item Pesanan</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  {(showDetail.items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <div>
                        <span className="text-gray-700">{item.name} ×{item.quantity}</span>
                        {item.duration && (
                          <span className="ml-2 text-[10px] text-gray-400 inline-flex items-center gap-0.5"><Clock size={10} /> {item.duration} menit</span>
                        )}
                      </div>
                      <span className="font-medium">{formatPrice(item.unit_price * (item.quantity || 1))}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2 space-y-1">
                    <div className="flex justify-between text-xs text-gray-500"><span>Subtotal</span><span>{formatPrice(showDetail.subtotal)}</span></div>
                    {showDetail.tax_amount > 0 && <div className="flex justify-between text-xs text-gray-500"><span>Pajak</span><span>{formatPrice(showDetail.tax_amount)}</span></div>}
                    {showDetail.discount_amount > 0 && <div className="flex justify-between text-xs text-gray-500"><span>Diskon</span><span>-{formatPrice(showDetail.discount_amount)}</span></div>}
                    <div className="flex justify-between text-sm font-bold pt-2 border-t"><span>Total</span><span className="text-brand">{formatPrice(showDetail.total)}</span></div>
                  </div>
                </div>
                {/* Total duration estimate */}
                {(showDetail.items || []).some(item => item.duration) && (
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <Clock size={12} />
                    Est. total pengerjaan: {(showDetail.items || []).reduce((sum, item) => sum + (parseInt(item.duration) || 0) * (item.quantity || 1), 0)} menit
                  </p>
                )}
              </div>

              <div><h3 className="text-sm font-semibold text-gray-700 mb-3">Workflow Status</h3><div className="flex flex-wrap gap-1">{STATUSES.map(s => (<span key={s} className={`px-2 py-1 text-xs rounded-full font-medium ${showDetail.status === s ? (s === 'cancelled' || s === 'invoice_outstanding' ? 'bg-red-600 text-white' : 'bg-brand text-white') : 'bg-gray-100 text-gray-400'}`}>{statusLabels[s] || s}</span>))}</div><div className="flex items-center gap-2 mt-3 text-xs text-gray-500"><span>Dibuat: {fmtDate(showDetail.created_at)}</span>{showDetail.scheduled_at && <span>· Jadwal: {fmtDate(showDetail.scheduled_at)}</span>}</div></div>
            </div>
          </div>
        </div>
      )}

      {invoiceOrder && <InvoiceModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)} formatPrice={formatPrice} fmtDate={fmtDate} />}

      {/* Payment Status Change Modal — Note required + Image optional */}
      {paymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b">
              <h3 className="font-bold text-gray-800 text-lg">💳 Ubah Status Pembayaran</h3>
              <button onClick={() => setPaymentModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm">
                <p className="font-semibold text-yellow-800">Status akan diubah ke: <span className="text-brand">{paymentLabels[paymentModal.value]}</span></p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Catatan <span className="text-red-500">*</span></label>
                <textarea
                  value={paymentModal.note}
                  onChange={e => setPaymentModal(prev => ({ ...prev, note: e.target.value }))}
                  placeholder="Contoh: Sudah dibayar lunas via transfer BCA tgl 04/07/2026..."
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand outline-none resize-none"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Upload Bukti (opsional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setPaymentModal(prev => ({
                        ...prev,
                        image: file,
                        imagePreview: URL.createObjectURL(file)
                      }));
                    }
                  }}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand/10 file:text-brand hover:file:bg-brand/20"
                />
                {paymentModal.imagePreview && (
                  <div className="mt-3 relative inline-block">
                    <img src={paymentModal.imagePreview} alt="Preview" className="h-24 rounded-lg border" />
                    <button
                      onClick={() => setPaymentModal(prev => ({ ...prev, image: null, imagePreview: null }))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                    >✕</button>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setPaymentModal(null)}
                  className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl text-gray-600 font-semibold text-sm hover:bg-gray-50 transition"
                >Batal</button>
                <button
                  onClick={handlePaymentSubmit}
                  disabled={saving}
                  className="flex-1 py-2.5 bg-brand text-white rounded-xl font-bold text-sm hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                >{saving ? '⏳ Menyimpan...' : '✅ Simpan'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}